import { useMemo, useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useSuperuserGuard } from "@/hooks/use-superuser-guard";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Factory, Search, RotateCw } from "lucide-react";
import { toast } from "sonner";
import {
  ProductionDeviceCard,
  type ProductionDevice,
} from "@/components/devices/ProductionDeviceCard";

const PRODUCTION_ORG_ID = "7a4a3ed7-f614-4e4a-9eeb-dba745740c35";

const Production = () => {
  useSuperuserGuard();
  const [search, setSearch] = useState("");

  const { data: devices = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["production-devices", PRODUCTION_ORG_ID],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_details")
        .select(
          "device_id, device_name, device_serial, device_firmware, device_last_updated, device_status, imagekit, battery, cellular, camera_light_level, ambient_lux, device_description, camera_image_quality, images_allowed, images_current, group_name, group_reference, past_images, camera_config, enable_production_test"
        )
        .eq("organization_id", PRODUCTION_ORG_ID)
        .order("device_last_updated", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data || []) as ProductionDevice[];
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) => {
      return (
        (d.device_name || "").toLowerCase().includes(q) ||
        (d.device_serial || "").toLowerCase().includes(q) ||
        (d.device_id || "").toLowerCase().includes(q)
      );
    });
  }, [devices, search]);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader
            title="Quality Control"
            description="Devices awaiting testing and assignment"
            icon={Factory}
          />
          <div
            className="px-4 py-6 md:p-8 space-y-4"
            style={{ paddingTop: "8.5rem" }}
          >
            <div className="flex items-center gap-2">
              <div className="relative w-full max-w-md">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by name or serial…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="text-sm text-muted-foreground whitespace-nowrap">
                {filtered.length} / {devices.length}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await refetch();
                  toast.success("Updated");
                }}
                disabled={isFetching}
                className="ml-auto"
              >
                <RotateCw
                  className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            </div>

            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading devices…</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No devices found.
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((device) => (
                  <ProductionDeviceCard
                    key={device.device_id}
                    device={device}
                    onChanged={() => refetch()}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Production;